import { useState, type PropsWithChildren } from "react";

import { $enum } from "ts-enum-util";

import dayjs from "@eisox/dayjs";
import { Badge, Modal, ToggleGroup } from "@eisox/design-system";
import { CrossIcon, PointChartIcon } from "@eisox/icons";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { keepPreviousData, useQuery, useSuspenseQuery } from "@tanstack/react-query";

import { buildingApi, equipmentApi } from "~/api";
import { DayRangePicker } from "~/components/day-range-picker";
import { HybridTooltip } from "~/components/hybrid-tooltip";
import { todayIn, toInstantRange } from "~/helpers/day-range.helper";
import { m } from "~/paraglide/messages";

import { CONNECTIVITY_HISTORY_TABS, CONNECTIVITY_STATES } from "../connectivity-history.constant";
import {
  clampConnectivityHistoryRange,
  getConnectivityHistoryDefaultRange,
} from "../helpers/connectivity-history.helper";
import { ConnectivityHistoryTab } from "../types/connectivity-history.type";
import { ConnectivityAvailability } from "./connectivity-availability";
import { ConnectivityPingsTable } from "./connectivity-pings-table";

interface ConnectivityHistoryProps {
  buildingId: string;
  equipmentId: string;
  label: string;
  mac: string;
}

const ConnectivityHistory: React.FC<ConnectivityHistoryProps> = ({ buildingId, equipmentId, label, mac }) => {
  const { data: building } = useSuspenseQuery(buildingApi.queries.byId(buildingId));
  const today = todayIn(building.timezone);
  const [tab, setTab] = useState(ConnectivityHistoryTab.AVAILABILITY);
  const [range, setRange] = useState(() => getConnectivityHistoryDefaultRange(today));
  const instantRange = toInstantRange(range, building.timezone);
  const { data: availability } = useQuery({
    ...equipmentApi.queries.availability(buildingId, equipmentId, instantRange),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <Modal.Header
        title={m.connectivity_history_title()}
        subtitle={CONNECTIVITY_HISTORY_TABS[tab].subtitle()}
        icon={<PointChartIcon />}
      >
        <Modal.Close asChild>
          <CrossIcon className="text-primary-dark-gray" />
        </Modal.Close>
      </Modal.Header>
      <div className="gap-4 md:flex-row md:items-center flex flex-col flex-wrap items-stretch justify-between">
        <div className="gap-2.5 flex flex-wrap items-center">
          <p className="font-semibold [&_span]:font-light text-base [&_span]:text-sm [&_span]:italic">
            {label} <span>({mac})</span>
          </p>
          {availability && (
            <HybridTooltip
              content={
                availability.lastPingAt
                  ? m.connectivity_history_last_message({ ago: dayjs(availability.lastPingAt).fromNow() })
                  : m.connectivity_history_never_received()
              }
              position="top"
              keepHoverOpen={false}
            >
              <Badge status={CONNECTIVITY_STATES[availability.state].badge}>
                {CONNECTIVITY_STATES[availability.state].label()}
              </Badge>
            </HybridTooltip>
          )}
        </div>
        <div className="gap-5 min-w-0 md:flex-initial flex flex-1 flex-wrap items-center">
          <ToggleGroup.Root
            value={tab}
            onValueChange={value => {
              if ($enum(ConnectivityHistoryTab).isValue(value)) setTab(value);
            }}
          >
            {$enum(ConnectivityHistoryTab)
              .getValues()
              .map(value => (
                <ToggleGroup.Item key={value} value={value}>
                  {CONNECTIVITY_HISTORY_TABS[value].label()}
                </ToggleGroup.Item>
              ))}
          </ToggleGroup.Root>
          <DayRangePicker
            value={range}
            onValueChange={selection => setRange(clampConnectivityHistoryRange(selection))}
            max={today}
            className="max-md:flex-col max-md:items-stretch md:w-80 max-md:[&>span]:hidden"
          />
        </div>
      </div>
      {tab === ConnectivityHistoryTab.AVAILABILITY ? (
        availability && <ConnectivityAvailability availability={availability} timezone={building.timezone} />
      ) : (
        <ConnectivityPingsTable
          buildingId={buildingId}
          equipmentId={equipmentId}
          range={instantRange}
          timezone={building.timezone}
        />
      )}
    </>
  );
};

interface ConnectivityHistoryDialogProps extends PropsWithChildren, Modal.RootProps, ConnectivityHistoryProps {}

const ConnectivityHistoryDialog: React.FC<ConnectivityHistoryDialogProps> = ({
  children,
  buildingId,
  equipmentId,
  label,
  mac,
  open: openProp,
  onOpenChange,
  defaultOpen,
  ...props
}) => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen ?? false,
  });

  return (
    <Modal.Root {...props} open={open} onOpenChange={setOpen}>
      <Modal.Trigger asChild>{children}</Modal.Trigger>
      <Modal.Content className="gap-6 flex h-[85dvh] w-[95vw] flex-col">
        <ConnectivityHistory buildingId={buildingId} equipmentId={equipmentId} label={label} mac={mac} />
      </Modal.Content>
    </Modal.Root>
  );
};

ConnectivityHistoryDialog.displayName = "ConnectivityHistoryDialog";

export { ConnectivityHistoryDialog };
